import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, CircularProgress, Snackbar, Button } from '@mui/material';
import { IconRefresh, IconReload, IconInfoCircle } from '@tabler/icons-react';
import type {
  Vendor,
  VendorImportBatch,
  UnmatchedWorklist,
  UnmatchedWorklistItem,
  BillingTerm,
} from '@techgitter/contracts';
import { getVendors } from '../../services/vendorService';
import {
  fetchUnmatchedWorklist,
  fetchImportBatches,
  triggerReconcile,
} from '../../services/reconciliationApi';
import { CreateVendorProductDialog } from './CreateVendorProductDialog';
import { CreateAliasDialog } from './CreateAliasDialog';
import { textMatches } from './reconciliationFilter';
import { ReconciliationFilterFields } from './ReconciliationFilterFields';
import {
  SecondaryToolbar,
  ToolbarSelect,
  ToolbarSpacer,
  ToolbarButton,
} from '../toolbar';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../table';
import { Chip } from '../chip';
import { CARD_SX, OUTLINED_BTN_SX } from '../../styles/designSystem';

const ACTION_BTN_SX = { ...OUTLINED_BTN_SX, textTransform: 'none', fontSize: 12, py: 0.25, px: 1, minWidth: 0 };

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

interface Props {
  canCreate: boolean;
  customerFilter?: string;
  productFilter?: string;
  onCustomerFilterChange?: (v: string) => void;
  onProductFilterChange?: (v: string) => void;
}

/**
 * Reference-data worklist: for a subscription supplier, the distinct unmatched
 * customer names and SKUs from its latest import (with row counts), each with a
 * create action that shrinks the unmatched backlog. Answers "where do I see the
 * 289 unmatched, and how do I act on them?".
 */
export const WorklistPanel: React.FC<Props> = ({
  canCreate,
  customerFilter = '',
  productFilter = '',
  onCustomerFilterChange,
  onProductFilterChange,
}) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorId, setVendorId] = useState<string>('');
  const [batch, setBatch] = useState<VendorImportBatch | null>(null);
  const [worklist, setWorklist] = useState<UnmatchedWorklist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createProduct, setCreateProduct] = useState<
    { sku: string; term?: BillingTerm | null } | null
  >(null);
  const [createAliasName, setCreateAliasName] = useState<string | null>(null);

  const [reconciling, setReconciling] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const selectedVendor = useMemo(
    () => vendors.find((v) => v.vendorId === vendorId),
    [vendors, vendorId],
  );

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendorId, label: v.name })),
    [vendors],
  );

  // Load subscription-supplier vendors once; default to the first.
  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        const suppliers = all.filter((v) => v.isSubscriptionSupplier);
        setVendors(suppliers);
        if (suppliers.length && !vendorId) setVendorId(suppliers[0].vendorId);
      })
      .catch((e) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError('Could not load subscription suppliers.');
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!vendorId) return;
      setLoading(true);
      setError(null);
      try {
        const [batches, wl] = await Promise.all([
          fetchImportBatches({ vendorId, top: 1 }, signal),
          fetchUnmatchedWorklist({ vendorId }, signal),
        ]);
        if (signal?.aborted) return;
        setBatch(batches[0] ?? null);
        setWorklist(wl);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load worklist');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [vendorId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // The shared Customer filter narrows the unmatched-customers list; the
  // Product filter narrows the unmatched-SKUs list. Each acts on its own list.
  const filteredCustomers = useMemo(
    () => (worklist?.customers ?? []).filter((c) => textMatches(customerFilter, c.value)),
    [worklist, customerFilter],
  );
  const filteredSkus = useMemo(
    () => (worklist?.skus ?? []).filter((s) => textMatches(productFilter, s.value)),
    [worklist, productFilter],
  );

  const rerunReconcile = useCallback(
    async (scope?: { onlySku?: string; onlyCustomer?: string }) => {
    if (!vendorId || reconciling) return;
    setReconciling(true);
    setError(null);
    try {
      // Pass vendorId so the server reconciles the CURRENT latest batch (a batch
      // id held in this panel can be stale after a re-import/delete). A `scope`
      // (onlySku / onlyCustomer) re-evaluates just the rows for one new piece of
      // reference data instead of the whole batch.
      const result = await triggerReconcile({ vendorId, ...scope });
      if (result.running) {
        // Large batches run past the request window; the engine finishes
        // server-side. Inform + auto-refresh shortly so the result appears.
        setToast(
          result.message ??
            'Reconcile started — still running; refreshing shortly.',
        );
        window.setTimeout(() => load(), 30_000);
      } else {
        setToast(`Reconcile complete — ${result.processed ?? 0} batch(es) processed.`);
        await load();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  },
    [vendorId, reconciling, load],
  );

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Supplier"
          value={vendorId}
          onChange={setVendorId}
          options={vendorOptions}
        />
        {batch && (
          <Box
            component="span"
            title={`Latest import: ${fmtDate(batch.importedAt)} · ${batch.rowCount ?? '—'} rows`}
            sx={{ color: '#888', display: 'flex', alignItems: 'center', cursor: 'default' }}
          >
            <IconInfoCircle size={16} stroke={1.5} />
          </Box>
        )}
        <ReconciliationFilterFields
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={onCustomerFilterChange ?? (() => {})}
          onProductFilterChange={onProductFilterChange ?? (() => {})}
        />
        <ToolbarSpacer />
        {canCreate && (
          <ToolbarButton icon={IconReload} onClick={() => rerunReconcile()}>
            {reconciling ? 'Reconciling…' : 'Re-run reconcile'}
          </ToolbarButton>
        )}
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !worklist || (!worklist.customers.length && !worklist.skus.length) ? (
          <Typography sx={{ fontSize: 13, color: '#888', px: 0.5, py: 2 }}>
            No unmatched reference data for this supplier’s latest import. 🎉
          </Typography>
        ) : !filteredCustomers.length && !filteredSkus.length ? (
          <Typography sx={{ fontSize: 13, color: '#888', px: 0.5, py: 2 }}>
            No unmatched reference data matches the current filters.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <WorklistTable
              title="Unmatched customers"
              subtitle="Names with no alias — create one to map them to a customer."
              actionLabel="Create alias"
              items={filteredCustomers}
              canCreate={canCreate}
              onAction={(item) => setCreateAliasName(item.value)}
            />
            <WorklistTable
              title="Unmatched SKUs"
              subtitle="Offer names with no vendor product — create one to match them. Monthly and annual are listed separately."
              actionLabel="Create product"
              items={filteredSkus}
              showTerm
              canCreate={canCreate}
              onAction={(item) => setCreateProduct({ sku: item.value, term: item.term })}
            />
          </Box>
        )}
      </Box>

      {createProduct !== null && selectedVendor && (
        <CreateVendorProductDialog
          open
          vendorId={selectedVendor.vendorId}
          vendorName={selectedVendor.name}
          initialSku={createProduct.sku}
          initialBillingTerm={createProduct.term ?? undefined}
          onClose={() => setCreateProduct(null)}
          onCreated={(opts) => {
            setCreateProduct(null);
            // Scoped reconcile flips just this SKU's rows; else just refresh.
            if (opts.reconcile) rerunReconcile({ onlySku: opts.sku });
            else load();
          }}
        />
      )}
      {createAliasName !== null && selectedVendor && (
        <CreateAliasDialog
          open
          vendorId={selectedVendor.vendorId}
          vendorName={selectedVendor.name}
          initialAlias={createAliasName}
          onClose={() => setCreateAliasName(null)}
          onCreated={(opts) => {
            setCreateAliasName(null);
            // Scoped reconcile flips just this customer's rows; else just refresh.
            if (opts.reconcile) rerunReconcile({ onlyCustomer: opts.identifier });
            else load();
          }}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </>
  );
};

const WorklistTable: React.FC<{
  title: string;
  subtitle: string;
  actionLabel: string;
  items: UnmatchedWorklistItem[];
  canCreate: boolean;
  showTerm?: boolean;
  onAction: (item: UnmatchedWorklistItem) => void;
}> = ({ title, subtitle, actionLabel, items, canCreate, showTerm, onAction }) => {
  const colSpan = 1 + (showTerm ? 1 : 0) + 1 + (canCreate ? 1 : 0);
  return (
    <Box sx={[CARD_SX, { flex: '1 1 360px', minWidth: 0, overflow: 'hidden' }]}>
      <Box sx={{ p: '12px 16px', borderBottom: '0.5px solid #e2eaf3' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{title}</Typography>
          <Chip>{items.length}</Chip>
        </Box>
        <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>{subtitle}</Typography>
      </Box>
      <DataTable>
        <thead>
          <tr>
            <DataTableHeadCell>Value</DataTableHeadCell>
            {showTerm && <DataTableHeadCell>Term</DataTableHeadCell>}
            <DataTableHeadCell align="right">Rows</DataTableHeadCell>
            {canCreate && <DataTableHeadCell align="right">Action</DataTableHeadCell>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <DataTableEmpty colSpan={colSpan}>None.</DataTableEmpty>
          ) : (
            items.map((item) => (
              <DataTableRow key={`${item.value}|${item.term ?? ''}`}>
                <DataTableCell>{item.value}</DataTableCell>
                {showTerm && (
                  <DataTableCell>{item.term ? <Chip>{item.term}</Chip> : '—'}</DataTableCell>
                )}
                <DataTableCell align="right">{item.count}</DataTableCell>
                {canCreate && (
                  <DataTableCell align="right">
                    <Button size="small" variant="outlined" sx={ACTION_BTN_SX} onClick={() => onAction(item)}>
                      {actionLabel}
                    </Button>
                  </DataTableCell>
                )}
              </DataTableRow>
            ))
          )}
        </tbody>
      </DataTable>
    </Box>
  );
};
